import { InputGroup } from '@blueprintjs/core'
import SearchIcon from '@material-ui/icons/Search'
import _, { size } from 'lodash'
import React, { useState } from 'react'
import styled from 'styled-components'
import { formatBRL } from '../../../cash-register/common/formatBRL'
import { blue, white } from '../../../ui/utils/_colors'
import { calculateDownPayment, calculateMaxAmountPlusDownPayment, isProductWithPriceRange } from './ProductsHelper'
import { convertToPercentage } from '../../../utils/math.utils'

export interface Product {
  id: string
  name: string
  price: number
  maxDownPaymentRate: number
  downPayment?: number
  priceMinusDownPayment?: number
  minPriceRate?: number
}

export type Layout = 'responsive' | 'single-column'
interface ProductSessionProps {
  products: Product[]
  onProductSelect?: (product: Product) => void
  canSelectItems?: boolean
  isSelectItemDisabled?: boolean
  layout?: Layout
}

interface Filter {
  name: string
}

export const ProductsSession: React.FC<ProductSessionProps> = (props: ProductSessionProps) => {
  const { canSelectItems, isSelectItemDisabled, layout = 'responsive' } = props
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [filterValue, setFilterValue] = useState<Filter | undefined>(undefined)

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product)

    if (props.onProductSelect) {
      props.onProductSelect(product)
    }
  }

  const onNameFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue({ ...filterValue, name: e.currentTarget.value.toUpperCase() })
  }

  const applyFilter = (list: Product[], filter?: Filter): Product[] => {
    if (!filter) return list

    return list.filter(product => {
      let result = true
      if (filter.name) {
        const splittedNameFilter = filter.name.replace(/\s\s+/g, ' ').split(' ')
        const productName = product.name.toUpperCase()
        result = result && splittedNameFilter.every(word => productName.includes(word))
      }
      return result
    })
  }

  const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
    const productHasPriceRange = isProductWithPriceRange(product)
    return (
      <Card>
        <ListContainer isSelectItemDisabled={isSelectItemDisabled}>
          {canSelectItems ? (
            <InputRadioContainer>
              <InputRadio
                id="selectProduct"
                name="products"
                type="radio"
                checked={_.isEqual(selectedProduct, product)}
                onChange={() => handleSelectProduct(product)}
                disabled={isSelectItemDisabled}
              />
            </InputRadioContainer>
          ) : null}
          <div>
            <ProductTitle>{product.name}</ProductTitle>
            {productHasPriceRange && canSelectItems && (
              <DownPaymentTag>Entrada fixa de {convertToPercentage(product.maxDownPaymentRate, 0)}%</DownPaymentTag>
            )}
            {!productHasPriceRange && (
              <>
                <Price>{formatBRL(product.price)}</Price>
                <DownPaymentTag>Entrada de {formatBRL(calculateDownPayment(product.price, product.maxDownPaymentRate))}</DownPaymentTag>
              </>
            )}
          </div>
        </ListContainer>
      </Card>
    )
  }

  const ProductList: React.FC<{ products: Product[] }> = (props: { products: Product[] }) => (
    <List layout={layout}>
      {props.products.map((product, index) => (
        <ProductCard key={index} product={product} />
      ))}
    </List>
  )

  const sortedProducts = props.products.sort((a, b) => { return a.name.localeCompare(b.name) })

  return (
    <MainContainer>
      <ProductsSection>
        <Title>Celulares disponíveis</Title>
        <FilterContainer>
          <TextInput
            placeholder="Busque pelo nome do aparelho..."
            type={'text'}
            tabIndex={1}
            autoComplete="off"
            value={filterValue?.name}
            onChange={onNameFilterChange}
            rightElement={<TextInputIcon />}
          />
        </FilterContainer>
        <Scroll>
          <ProductList 
            products={applyFilter(sortedProducts, filterValue)}
          />
        </Scroll>
      </ProductsSection>
    </MainContainer>
  )
}

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100% - 100px);
`

const Title = styled.h1`
  font-family: Inter;
  font-size: 22px;
  line-height: 28px;
  font-weight: bold;
  color: black;
  text-align: left;
  padding-top: 0.5rem;
  margin-bottom: 1rem;
`

const FilterContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin: 16px 0px 24px 0px;
`
const baseWidth = 400;

const TextInput = styled(InputGroup)`
  width: ${baseWidth}px;
  margin-left: 2px;
  @media (max-width: ${baseWidth}px) {
    width: ${baseWidth - 100}px;
  }
`

const TextInputIcon = styled(SearchIcon)`
    color: var(--Grayscale-500, #AAB6BF);
    text-align: center;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    margin: 4px 4px;
`

const ListContainer = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 20px;
  align-items: center;
  gap: 16px;
  opacity: ${({ isSelectItemDisabled }: { isSelectItemDisabled: boolean }) => isSelectItemDisabled ? 0.3 : 1};
`

const Card = styled.div`
  border: 1px solid #CDD6DD;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-family: Inter;
`

const calculateMaxWidth = (horizontalSpacing: number, baseWidth: number, multiplier: number): number => {
  return horizontalSpacing + baseWidth * multiplier
}

const horizontalSpacing = 100

const List = styled.div<{ layout: Layout }>`
  display: grid;
  ${({ layout }: { layout: Layout }) => {
    if (layout == 'responsive') {
      return `
    grid-template-columns: repeat(4, 1fr);

    @media (max-width: ${calculateMaxWidth(horizontalSpacing, baseWidth, 4)}px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: ${calculateMaxWidth(horizontalSpacing, baseWidth, 3)}px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: ${calculateMaxWidth(horizontalSpacing, baseWidth, 2)}px) {
      grid-template-columns: 1fr;
    }
  `
    } else if (layout == 'single-column') {
      return `
    grid-template-columns: 1fr;
  `
    }
  }}
`

const ProductTitle = styled.h3`
  font-size: 14px;
  line-height: 18px;
  font-weight: 400;
  margin-bottom: 8px;
  text-align: left;
`

const Price = styled.p`
  color: #182026;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  text-align: left;
  margin-bottom: 8px;
`

const DownPaymentTag = styled.div`
  display: inline-block;
  padding: 4px 12px;
  background: #E5EDFF;
  border-radius: 100px;
  border: 1px solid ${blue.yonder};
  color: ${blue.yonder};
  font-family: Inter;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
` 

const InputRadio = styled.input`
  width: 18px;
  height: 18px;
  accent-color: green;
`

const InputRadioContainer = styled.div`
  width: 18px;
  height: 18px;
`

const Scroll = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  height: calc(80%);
  @media (min-width: 480px) {
    ::-webkit-scrollbar {
      width: 12px;
    }
    ::-webkit-scrollbar-track {
      background: #f1f1f1;
    }
    ::-webkit-scrollbar-thumb {
      background-color: #888;
      border-radius: 10px;
      border: 3px solid #f1f1f1;
    }
    ::-webkit-scrollbar-thumb:hover {
      background-color: #555;
    }
  }
`

const ProductsSection = styled.div`
  flex: 1;
`